import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../utils/site-config'

let SDK = null
let loading = false
let currentUserId = null

// Check whether Braze debug logging is enabled.
const isBrazeDebugEnabled = () => localStorage.getItem('BRAZE_ENABLE_LOGGING') === 'true'

/**
 * Exposes a limited Braze debug API on the global `window` object when debug logging is enabled.
 * Allows manual triggering of Braze custom events from the browser console during development.
 *
 * @param {import('@braze/web-sdk')} braze - The initialized Braze SDK instance.
 * @returns {void}
 */
function exposeBrazeDebug(braze) {
    if (!isBrazeDebugEnabled()) return

    window.BrazeDebug = {
        logCustomEvent: (...args) => braze.logCustomEvent(...args)
    }
}

/**
 * Removes the `window.BrazeDebug` object if it was previously set by {@link exposeBrazeDebug}.
 * Called during Braze shutdown to clean up any debug references from the global scope.
 *
 * @returns {void}
 */
function cleanupBrazeDebug() {
    if (window.BrazeDebug) {
        delete window.BrazeDebug
    }
}

/**
 * Initializes the Braze Web SDK and starts a session for the given user.
 * If the SDK is already initialized and the `userId` has changed, updates the active user.
 * Concurrent calls while initialization is in progress are ignored.
 *
 * @param {string | null | undefined} userId - The SFCC customer ID to associate with the Braze profile.
 * @param {string} [siteId] - The current SFCC site ID. When omitted, falls back to the site
 *   resolved from the runtime commerceAPI config. Required so the site-scoped Braze env vars
 *   (`IS_BRAZE_ENABLED`, `BRAZE_API_KEY`, `BRAZE_BASE_URL`) are read for the right site.
 * @returns {Promise<import('@braze/web-sdk') | null>} The initialized Braze SDK instance, or `null`
 *   if Braze is disabled, misconfigured, already loading, or initialization fails.
 */
export async function initBraze(userId, siteId) {
    if (SDK) {
        if (userId && userId !== currentUserId) {
            SDK.changeUser(userId)
            currentUserId = userId
        }

        return SDK
    }

    // Prevent duplicate SDK initialization
    if (loading) return null

    const {app} = getConfig()
    const resolvedSiteId = siteId || app?.commerceAPI?.parameters?.siteId
    const isEnabled = getSiteEnvVarValue(app, 'IS_BRAZE_ENABLED', resolvedSiteId) === 'true'
    const apiKey = getSiteEnvVarValue(app, 'BRAZE_API_KEY', resolvedSiteId)
    const baseUrl = getSiteEnvVarValue(app, 'BRAZE_BASE_URL', resolvedSiteId)

    if (!isEnabled || !apiKey || !baseUrl) {
        return null
    }

    loading = true

    try {
        const braze = await import('@braze/web-sdk')

        // Initialize Braze SDK
        braze.initialize(apiKey, {
            baseUrl,
            enableLogging: isBrazeDebugEnabled(),
            allowUserSuppliedJavascript: false
        })

        // Attach logged-in user to Braze profile
        if (userId) {
            braze.changeUser(userId)
            currentUserId = userId
        }

        // Start Braze session
        braze.openSession()
        SDK = braze

        // Expose limited debug API only when logging is enabled
        exposeBrazeDebug(braze)

        return SDK
    } catch (err) {
        console.error('Braze failed to initialize', err)
        return null
    } finally {
        loading = false
    }
}

/**
 * Logs a custom event to Braze. No-op if the SDK has not been initialized.
 *
 * @param {string} eventName - The name of the custom event to log.
 * @param {Record<string, unknown>} [properties] - Optional key-value properties to attach to the event.
 * @returns {void}
 */
export function logBrazeEvent(eventName, properties) {
    SDK?.logCustomEvent(eventName, properties)
    isBrazeDebugEnabled() && console.log('Braze -', eventName, properties)
}

/**
 * Shuts down the active Braze SDK session, resets all module-level state,
 * and removes the debug API from the global scope.
 * No-op if the SDK has not been initialized.
 *
 * @returns {void}
 */
export function stopBraze() {
    if (!SDK) return

    try {
        // Destroy active Braze session
        SDK.destroy()
    } catch (err) {
        console.error('Braze shutdown failed', err)
    }

    SDK = null
    currentUserId = null

    cleanupBrazeDebug()
}
